import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Table,
  Button,
  TextInput,
  Badge,
  majorScale,
  Spinner,
  Alert,
  IconButton,
  CrossIcon,
  TickIcon,
  Heading,
  Text,
  SelectMenu,
} from 'evergreen-ui';
import { useCsvImport } from './hooks/useCsvImport';

const ValidationResults = ({ importSession, onComplete, onError }) => {
  const [validationResults, setValidationResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [corrections, setCorrections] = useState({});
  const [progress, setProgress] = useState(0);
  const [skippedRows, setSkippedRows] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const { validateCsvData, correctValidationErrors, getValidationProgress, getFieldOptions } = useCsvImport();

  useEffect(() => {
    const startValidation = async () => {
      try {
        validateCsvData(importSession.id)
          .then(results => {
            setValidationResults(results);
            setIsLoading(false);
          })
          .catch(error => {
            onError(error.message);
            setIsLoading(false);
          });
      } catch (error) {
        onError(error.message);
        setIsLoading(false);
      }
    };

    startValidation();
  }, [importSession.id]);

  useEffect(() => {
    if (!isLoading) return;

    const pollInterval = setInterval(async () => {
      try {
        const progressData = await getValidationProgress(importSession.id);
        if (progressData && progressData.progress_percentage !== undefined) {
          setProgress(progressData.progress_percentage);
        }
      } catch (error) {
        console.error('Error fetching validation progress:', error);
      }
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [isLoading, importSession.id, getValidationProgress]);

  // Load field options for specific fields when validation results are loaded
  useEffect(() => {
    if (isLoading || validationResults.length === 0) return;

    // Get unique field names with reference errors
    const fieldsToLoad = [...new Set(
      validationResults
        .filter(result => result.error_type === 'reference')
        .map(result => result.field_name)
    )];

    // Load options for each field
    fieldsToLoad.forEach(async (fieldName) => {
      if (fieldOptions[fieldName] || loadingOptions[fieldName]) return;

      setLoadingOptions(prev => ({ ...prev, [fieldName]: true }));
      try {
        const response = await getFieldOptions(importSession.id, fieldName);
        if (response && response.options) {
          setFieldOptions(prev => ({ ...prev, [fieldName]: response.options }));
        }
      } catch (error) {
        console.error(`Error loading options for ${fieldName}:`, error);
      } finally {
        setLoadingOptions(prev => ({ ...prev, [fieldName]: false }));
      }
    });
  }, [validationResults, isLoading, importSession.id, getFieldOptions]);

  const handleCorrectionChange = (resultId, value) => {
    setCorrections({
      ...corrections,
      [resultId]: value,
    });
  };

  const handleIgnore = (resultId) => {
    const result = validationResults.find((r) => r.id === resultId);
    if (result) {
      setValidationResults(
        validationResults.map((r) =>
          r.id === resultId ? { ...r, is_ignored: !r.is_ignored } : r
        )
      );

      if (!result.is_ignored) {
        const rowResults = validationResults.filter(r => r.row_index === result.row_index);

        if (!skippedRows.some(row => row.row_index === result.row_index)) {
          setSkippedRows([...skippedRows, {
            row_index: result.row_index,
            results: rowResults
          }]);
        }
      } else {
        setSkippedRows(skippedRows.filter(row => row.row_index !== result.row_index));
      }
    }
  };

  const handleSaveCorrections = async () => {
    try {
      setIsSaving(true);

      const correctionsToSave = Object.entries(corrections).map(([id, value]) => ({
        id,
        corrected_value: value,
      }));

      await correctValidationErrors(importSession.id, correctionsToSave);

      if (skippedRows.length > 0) {
        localStorage.setItem(`skipped_rows_${importSession.id}`, JSON.stringify(skippedRows));
      }

      onComplete();
    } catch (error) {
      onError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const getErrorCount = () => {
    return validationResults.filter(
      (r) => r.severity === 'error' && !r.is_corrected && !r.is_ignored
    ).length;
  };

  const getWarningCount = () => {
    return validationResults.filter(
      (r) => r.severity === 'warning' && !r.is_corrected && !r.is_ignored
    ).length;
  };

  const hasAllCorrections = () => {
    const errorsNeedingCorrection = validationResults.filter(
      (r) => r.severity === 'error' && !r.is_ignored
    );

    return errorsNeedingCorrection.every(
      (r) => corrections[r.id] || r.corrected_value
    );
  };

  // Render a correction input based on field type
  const renderCorrectionInput = (result) => {
    if (result.is_ignored) return null;

    // For reference errors with imprint field, show a dropdown
    if (result.field_name === 'imprint' && result.error_type === 'reference' && fieldOptions.imprint) {
      return (
        <SelectMenu
          title="Select Imprint"
          options={fieldOptions.imprint.map(imprint => ({
            label: imprint.name,
            value: imprint.name
          }))}
          selected={corrections[result.id] || result.corrected_value}
          onSelect={item => handleCorrectionChange(result.id, item.value)}
          closeOnSelect
        >
          <Button>
            {corrections[result.id] || result.corrected_value || 'Select Imprint...'}
          </Button>
        </SelectMenu>
      );
    }

    // For reference errors with format field, show a dropdown
    if (result.field_name === 'format' && (result.error_type === 'reference' || result.error_type === 'required') && fieldOptions.format) {
      return (
        <SelectMenu
          title="Select Format"
          options={fieldOptions.format.map(format => ({
            label: `${format.description} (${format.value})`,
            value: format.value
          }))}
          selected={corrections[result.id] || result.corrected_value}
          onSelect={item => handleCorrectionChange(result.id, item.value)}
          closeOnSelect
        >
          <Button>
            {corrections[result.id] || result.corrected_value || 'Select Format...'}
          </Button>
        </SelectMenu>
      );
    }

    // For reference errors with audience field, show a dropdown
    if (result.field_name === 'audience' && result.error_type === 'reference' && fieldOptions.audience) {
      return (
        <SelectMenu
          title="Select Audience"
          options={fieldOptions.audience.map(audience => ({
            label: `${audience.description} (${audience.value})`,
            value: audience.value
          }))}
          selected={corrections[result.id] || result.corrected_value}
          onSelect={item => handleCorrectionChange(result.id, item.value)}
          closeOnSelect
        >
          <Button>
            {corrections[result.id] || result.corrected_value || 'Select Audience...'}
          </Button>
        </SelectMenu>
      );
    }

    // For numeric fields, use a numeric input
    if (['edition', 'volume', 'carton_qty', 'page_count'].includes(result.field_name)) {
      return (
        <TextInput
          type="number"
          value={corrections[result.id] || result.corrected_value || ''}
          onChange={e => handleCorrectionChange(result.id, e.target.value)}
          placeholder="Enter a number"
        />
      );
    }

    // For decimal fields, use a numeric input with step="0.01"
    if (['retail_price', 'wholesale_price'].includes(result.field_name)) {
      return (
        <TextInput
          type="number"
          step="0.01"
          value={corrections[result.id] || result.corrected_value || ''}
          onChange={e => handleCorrectionChange(result.id, e.target.value)}
          placeholder="Enter a decimal number"
        />
      );
    }

    // Default to text input for other fields
    return (
      <TextInput
        value={corrections[result.id] || result.corrected_value || ''}
        onChange={e => handleCorrectionChange(result.id, e.target.value)}
        placeholder="Enter correction"
      />
    );
  };

  if (isLoading) {
    return (
      <Pane display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={200}>
        <Heading size={500} marginBottom={majorScale(2)}>Validating CSV Data</Heading>
        <Pane width="100%" maxWidth={600} marginBottom={majorScale(2)} position="relative" height={8} background="#E4E7EB" borderRadius={4}>
          <Pane
            position="absolute"
            height={8}
            width={`${progress}%`}
            background="#47B881"
            borderRadius={4}
            transition="width 0.3s ease-in-out"
          />
        </Pane>
        <Text>{progress}% complete</Text>
        <Spinner marginTop={majorScale(2)} />
      </Pane>
    );
  }

  if (validationResults.length === 0) {
    return (
      <Pane>
        <Alert
          intent="success"
          title="Validation Successful"
          marginBottom={majorScale(2)}
        >
          No validation errors found. You can proceed to the next step.
        </Alert>
        <Pane display="flex" justifyContent="flex-end">
          <Button
            appearance="primary"
            onClick={() => onComplete()}
            marginRight={majorScale(2)}
          >
            Continue
          </Button>
        </Pane>
      </Pane>
    );
  }

  return (
    <Pane>
      <Pane marginBottom={majorScale(2)}>
        <Badge color="red" marginRight={majorScale(1)}>
          {getErrorCount()} Errors
        </Badge>
        <Badge color="yellow">
          {getWarningCount()} Warnings
        </Badge>
      </Pane>

      <Card
        backgroundColor="white"
        elevation={0}
        border="default"
        padding={majorScale(2)}
        marginBottom={majorScale(2)}
      >
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Row</Table.TextHeaderCell>
            <Table.TextHeaderCell>Field</Table.TextHeaderCell>
            <Table.TextHeaderCell>Type</Table.TextHeaderCell>
            <Table.TextHeaderCell>Message</Table.TextHeaderCell>
            <Table.TextHeaderCell>Original Value</Table.TextHeaderCell>
            <Table.TextHeaderCell>Correction</Table.TextHeaderCell>
            <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {validationResults.map((result) => (
              <Table.Row
                key={result.id}
                intent={result.is_ignored ? 'none' : result.severity === 'error' ? 'danger' : 'warning'}
              >
                <Table.TextCell>{result.row_index + 1}</Table.TextCell>
                <Table.TextCell>{result.field_name}</Table.TextCell>
                <Table.TextCell>
                  <Badge color={result.severity === 'error' ? 'red' : 'yellow'}>
                    {result.error_type}
                  </Badge>
                </Table.TextCell>
                <Table.TextCell>{result.message}</Table.TextCell>
                <Table.TextCell>{result.original_value}</Table.TextCell>
                <Table.Cell>
                  {renderCorrectionInput(result)}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    iconBefore={result.is_ignored ? TickIcon : CrossIcon}
                    intent={result.is_ignored ? 'success' : 'danger'}
                    onClick={() => handleIgnore(result.id)}
                    marginRight={majorScale(1)}
                    size="small"
                  >
                    {result.is_ignored ? "Restore Row" : "Skip Row"}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>

      <Pane display="flex" justifyContent="flex-end">
        <Button
          appearance="primary"
          onClick={handleSaveCorrections}
          disabled={!hasAllCorrections() || isSaving}
          isLoading={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Corrections & Continue'}
        </Button>
      </Pane>
    </Pane>
  );
};

export default ValidationResults;
